/**
 * Profit Booking for Multi-factor DEMA-WMA Gainer (owner spec 2026-10-26).
 *
 * Shows / edits `exits.profit_booking` + total lots of a selected config version. Save creates a NEW, INACTIVE
 * version copied from the selected one (nothing is activated; activation stays in "Config versions").
 * Targets are frozen on each position at its entry, so a change applies to NEW positions only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, IconButton, MenuItem, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import {
  profitBookingLiveConfirmation, type DemaWmaProfitBooking, type DemaWmaProfitTarget,
} from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { extractErrorMessage } from "@/utils/errors";

interface VersionRow { version: number; is_active: boolean }
interface Row { pct: string; lots: string }
interface Props { versions: VersionRow[]; activeVersion: number | null; onSaved?: (msg: string) => void }

const TICK = 0.05;

function targetPremium(entry: number, pct: number): number {
  const ticks = Math.ceil(Math.round(((entry * (1 + pct / 100)) / TICK) * 1e6) / 1e6);
  return Math.round(ticks * TICK * 100) / 100;
}

function toRows(t: DemaWmaProfitTarget[]): Row[] {
  return t.map((x) => ({ pct: String(x.pct), lots: String(x.lots) }));
}

function validate(rows: Row[], totalLots: string, maxTargets: number, maxPct: number): string[] {
  const errors: string[] = [];
  const total = Number(totalLots);
  if (!Number.isInteger(total) || total < 1 || total > 50) errors.push("Total lots must be a whole number from 1 to 50");
  if (rows.length < 1 || rows.length > maxTargets) errors.push(`Between 1 and ${maxTargets} targets`);
  let prev = 0;
  let booked = 0;
  rows.forEach((r, i) => {
    const pct = Number(r.pct);
    const lots = Number(r.lots);
    if (r.pct.trim() === "" || !Number.isFinite(pct) || pct <= 0 || pct > maxPct) errors.push(`Target ${i + 1}: % must be > 0 and <= ${maxPct}`);
    else if (pct <= prev) errors.push(`Target ${i + 1}: % must be higher than target ${i}`);
    else prev = pct;
    if (!Number.isInteger(lots) || lots < 1) errors.push(`Target ${i + 1}: lots must be a whole number >= 1`);
    else booked += lots;
  });
  if (Number.isInteger(total) && booked >= total) errors.push(`Targets book ${booked} of ${total} lot(s): at least 1 lot must remain for the algo exit`);
  return errors;
}

export default function DemaWmaProfitBookingCard({ versions, activeVersion, onSaved }: Props) {
  const demaWmaGainerService = useDemaWmaService();
  const [version, setVersion] = useState<number | null>(activeVersion);
  const [view, setView] = useState<DemaWmaProfitBooking | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [totalLots, setTotalLots] = useState("3");
  const [rows, setRows] = useState<Row[]>([]);
  const [live, setLive] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [sampleEntry, setSampleEntry] = useState("100");
  const [instrument, setInstrument] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reset = useCallback((v: DemaWmaProfitBooking) => {
    setEnabled(v.configured ? v.enabled : true);
    setTotalLots(String(v.configured ? v.total_lots : Math.max(v.total_lots, v.defaults.total_lots)));
    setRows(toRows(v.targets));
    setLive(v.live_enabled);
    setConfirm("");
    setInstrument((cur) => cur || Object.keys(v.lot_sizes)[0] || "");
  }, []);

  const load = useCallback(async (v: number | null) => {
    setError(null);
    try {
      const data = await demaWmaGainerService.getProfitBooking(v ?? undefined);
      setView(data);
      reset(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }, [reset, demaWmaGainerService]);

  useEffect(() => { void load(version); }, [load, version]);

  const nextVersion = useMemo(() => Math.max(0, ...versions.map((v) => v.version)) + 1, [versions]);
  const maxTargets = view?.max_targets ?? 5;
  const errors = view ? validate(rows, totalLots, maxTargets, view.max_pct) : [];
  const liveNeedsConfirm = live && !view?.live_enabled;
  const phrase = profitBookingLiveConfirmation(nextVersion);
  const lotSize = view?.lot_sizes[instrument] ?? 1;

  const preview = useMemo(() => {
    const entry = Number(sampleEntry);
    let left = Number(totalLots) || 0;
    const out = rows.map((r, i) => {
      const want = Number(r.lots) || 0;
      const lots = Math.min(want, Math.max(0, left - 1));
      left -= lots;
      return { n: i + 1, pct: Number(r.pct), lots, premium: entry > 0 && Number(r.pct) > 0 ? targetPremium(entry, Number(r.pct)) : null, skipped: lots < want };
    });
    return { rows: out, remaining: left };
  }, [rows, totalLots, sampleEntry]);

  const save = async () => {
    if (!view || errors.length) return;
    setSaving(true);
    setError(null);
    try {
      const res = await demaWmaGainerService.saveProfitBookingDraft({
        source_version: view.version, expected_version: nextVersion, enabled, total_lots: Number(totalLots),
        targets: rows.map((r) => ({ pct: Number(r.pct), lots: Number(r.lots) })), live_enabled: live,
        ...(liveNeedsConfirm ? { confirm } : {}),
      });
      const msg = `Profit booking saved as v${res.version} (INACTIVE). Activate it under Config versions; open positions keep their targets.`;
      setNotice(msg);
      onSaved?.(msg);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="profit-booking-card">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>Profit Booking</Typography>
          {view && <Chip size="small" data-testid="pb-status" label={view.status}
                         color={view.enabled ? (view.live_enabled ? "warning" : "success") : "default"} />}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField size="small" label="Strategy" value={view?.strategy ?? "Multi-factor DEMA-WMA Gainer"} InputProps={{ readOnly: true }} />
          <TextField select size="small" label="Config version" value={version ?? ""} sx={{ minWidth: 160 }}
                     onChange={(e) => setVersion(Number(e.target.value))} inputProps={{ "data-testid": "pb-version" }}>
            {versions.map((v) => (
              <MenuItem key={v.version} value={v.version}>v{v.version}{v.is_active ? " (active)" : ""}</MenuItem>
            ))}
          </TextField>
          {view && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }} data-testid="pb-updated">
              Last updated {view.updated_at ? new Date(view.updated_at).toLocaleString() : "—"}
              {view.activated_at ? ` · activated ${new Date(view.activated_at).toLocaleString()}` : ""}
            </Typography>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

        {view && (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
              <FormControlLabel control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                                                 inputProps={{ "aria-label": "Enable profit booking" }} />}
                                label="Enable profit booking" />
              <TextField size="small" type="number" label="Total lots" value={totalLots} sx={{ width: 120 }}
                         onChange={(e) => setTotalLots(e.target.value)} inputProps={{ min: 1, max: 50, "data-testid": "pb-total-lots" }} />
              <TextField size="small" label="Remaining quantity" sx={{ minWidth: 260 }} InputProps={{ readOnly: true }}
                         value="Existing algo exit (SL, indicator exits, square-off, kill switch)" />
            </Stack>

            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow><TableCell>Target</TableCell><TableCell>Premium gain %</TableCell><TableCell>Lots</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`pb-row-${i + 1}`}>
                    <TableCell>T{i + 1}</TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={r.pct} sx={{ width: 110 }}
                                 inputProps={{ step: 0.5, "aria-label": `Target ${i + 1} %` }}
                                 onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, pct: e.target.value } : x)))} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={r.lots} sx={{ width: 90 }}
                                 inputProps={{ min: 1, "aria-label": `Target ${i + 1} lots` }}
                                 onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, lots: e.target.value } : x)))} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" aria-label={`Remove target ${i + 1}`} disabled={rows.length <= 1}
                                  onClick={() => setRows(rows.filter((_, k) => k !== i))}>✕</IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button size="small" disabled={rows.length >= maxTargets} sx={{ mb: 2 }}
                    onClick={() => {
                      const last = rows.length ? Number(rows[rows.length - 1].pct) || 0 : 5;
                      setRows([...rows, { pct: String(last + 5), lots: "1" }]);
                    }}>
              + Add target
            </Button>

            <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: "action.hover" }} data-testid="pb-preview">
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600}>Preview</Typography>
                <TextField size="small" type="number" label="Avg entry premium" value={sampleEntry} sx={{ width: 160 }}
                           onChange={(e) => setSampleEntry(e.target.value)} inputProps={{ "data-testid": "pb-sample-entry" }} />
                {Object.keys(view.lot_sizes).length > 0 && (
                  <TextField select size="small" label="Index" value={instrument} sx={{ width: 140 }}
                             onChange={(e) => setInstrument(e.target.value)}>
                    {Object.entries(view.lot_sizes).map(([k, v]) => <MenuItem key={k} value={k}>{k} (lot {v})</MenuItem>)}
                  </TextField>
                )}
              </Stack>
              {preview.rows.map((p) => (
                <Typography key={p.n} variant="body2" data-testid={`pb-preview-${p.n}`}>
                  T{p.n}: +{p.pct}% → sell {p.lots} lot(s) ({p.lots * lotSize} qty) at ≥ {p.premium ?? "—"}
                  {p.skipped ? " (reduced: one lot always stays for the algo exit)" : ""}
                </Typography>
              ))}
              <Typography variant="body2" data-testid="pb-preview-remaining">
                Remaining: {preview.remaining} lot(s) ({preview.remaining * lotSize} qty) on the existing algo exit
              </Typography>
            </Box>

            <FormControlLabel sx={{ mb: 1 }}
              control={<Checkbox checked={live} onChange={(e) => setLive(e.target.checked)} inputProps={{ "aria-label": "Live profit booking" }} />}
              label="Book profits on LIVE positions too (off by default; paper always books when enabled)" />
            {liveNeedsConfirm && (
              <TextField fullWidth size="small" sx={{ mb: 2 }} label={`Type: ${phrase}`} value={confirm}
                         onChange={(e) => setConfirm(e.target.value)} inputProps={{ "data-testid": "pb-confirm" }} />
            )}
            {view.price_source !== "TICK" && (
              <Alert severity="warning" sx={{ mb: 2 }}>This version checks prices on candle close; profit booking needs tick prices.</Alert>
            )}
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="pb-errors">{errors.join("; ")}</Alert>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Save creates v{nextVersion} as an INACTIVE copy of v{view.version} (only profit booking and total lots change).
              Targets use each trade&apos;s actual average entry premium and apply to NEW positions only.
              Total lots is also the live lot size per customer once that version is active.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => void save()} data-testid="pb-save"
                      disabled={saving || errors.length > 0 || (liveNeedsConfirm && confirm.trim() !== phrase)}>
                Save as new version
              </Button>
              <Button onClick={() => reset(view)} disabled={saving} data-testid="pb-cancel">Cancel</Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
